import { createSignal, For, Show, Suspense } from "solid-js";
import { A, createAsync, useAction, revalidate, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import {
  Button, Card, CardContent, CardHeader, CardTitle, Badge, Skeleton,
  Input, Select
} from "~/components/ui";
import { getChannels, addChannel, toggleChannelMonitoring } from "~/server/bff";
import { CACHE_KEYS } from "~/server/bff/cache-keys";
import { formatRelativeTime } from "~/lib/utils";
import {
  ArrowLeft, Save, Plus, RefreshCw, CheckCircle, XCircle, Clock,
  AlertTriangle, Pause, Play, Loader2
} from "lucide-solid";

export const route = {
  preload: () => getChannels(),
} satisfies RouteDefinition;

export default function SourcesSettingsPage() {
  const [backfillLimit, setBackfillLimit] = createSignal("10");
  const [pollInterval, setPollInterval] = createSignal("60");
  const [channelUrl, setChannelUrl] = createSignal("");
  const [isAdding, setIsAdding] = createSignal(false);
  const [isRefreshing, setIsRefreshing] = createSignal(false);
  const [togglingChannelId, setTogglingChannelId] = createSignal<string | null>(null);

  const channels = createAsync(() => getChannels(), { deferStream: true });
  const addChannelAction = useAction(addChannel);
  const toggleMonitoringAction = useAction(toggleChannelMonitoring);

  const pollIntervalOptions = [
    { value: "15", label: "Every 15 minutes" },
    { value: "30", label: "Every 30 minutes" },
    { value: "60", label: "Every hour" },
    { value: "180", label: "Every 3 hours" },
    { value: "360", label: "Every 6 hours" },
  ];

  const backfillOptions = [
    { value: "5", label: "Last 5 videos" },
    { value: "10", label: "Last 10 videos" },
    { value: "25", label: "Last 25 videos" },
    { value: "50", label: "Last 50 videos" },
    { value: "100", label: "Last 100 videos" },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "healthy":
        return <CheckCircle class="h-4 w-4 text-green-500" />;
      case "error":
        return <XCircle class="h-4 w-4 text-red-500" />;
      case "pending":
        return <Clock class="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertTriangle class="h-4 w-4 text-muted-foreground" />;
    }
  };

  const handleAddChannel = async () => {
    const url = channelUrl().trim();
    if (!url) return;

    setIsAdding(true);
    try {
      const formData = new FormData();
      formData.set("youtubeUrl", url);
      await addChannelAction(formData);
      setChannelUrl("");
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleMonitoring = async (channelId: string, currentlyMonitored: boolean) => {
    setTogglingChannelId(channelId);
    try {
      const formData = new FormData();
      formData.set("channelId", channelId);
      formData.set("isMonitored", (!currentlyMonitored).toString());
      await toggleMonitoringAction(formData);
    } finally {
      setTogglingChannelId(null);
    }
  };

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      await revalidate(CACHE_KEYS.CHANNELS);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSaveSettings = () => {
    // Settings would be saved to a user_settings table in the future
    // For now, show that the values are captured
    console.log("[Settings] Poll interval:", pollInterval(), "Backfill limit:", backfillLimit());
  };

  return (
    <AppShell title="Sources Settings">
      <div class="p-6 max-w-4xl mx-auto">
        <div class="flex items-center gap-2 mb-6">
          <A
            href="/settings"
            class="text-muted-foreground hover:text-foreground"
            aria-label="Back to settings"
            title="Back to settings"
          >
            <ArrowLeft class="h-4 w-4" />
          </A>
          <Badge variant="secondary" class="text-xs font-mono">Settings</Badge>
        </div>

        <PageHeader
          title="Sources & Ingestion"
          description="Configure channel monitoring and ingestion settings"
        />

        <div class="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle class="text-lg">Default Ingestion Settings</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <label class="text-sm font-medium mb-2 block">Poll Interval</label>
                  <Select
                    options={pollIntervalOptions}
                    value={pollInterval()}
                    onChange={setPollInterval}
                  />
                  <p class="text-xs text-muted-foreground mt-1">
                    How often to check for new videos
                  </p>
                </div>
                <div>
                  <label class="text-sm font-medium mb-2 block">Backfill Limit</label>
                  <Select
                    options={backfillOptions}
                    value={backfillLimit()}
                    onChange={setBackfillLimit}
                  />
                  <p class="text-xs text-muted-foreground mt-1">
                    Videos to fetch when adding a new channel
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader class="flex flex-row items-center justify-between">
              <CardTitle class="text-lg">Ingestion Health</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefreshStatus}
                disabled={isRefreshing()}
              >
                <Show when={isRefreshing()} fallback={<RefreshCw class="h-4 w-4 mr-1" />}>
                  <Loader2 class="h-4 w-4 mr-1 animate-spin" />
                </Show>
                Refresh Status
              </Button>
            </CardHeader>
            <CardContent>
              <Suspense fallback={<HealthSkeleton />}>
                <Show when={channels()}>
                  {(chans) => (
                    <div class="space-y-3">
                      <div class="grid grid-cols-4 gap-4 mb-4">
                        <div class="text-center p-3 rounded-md bg-green-500/10">
                          <p class="text-2xl font-bold text-green-600">
                            {chans().filter((c) => c.isMonitored).length}
                          </p>
                          <p class="text-xs text-muted-foreground">Active</p>
                        </div>
                        <div class="text-center p-3 rounded-md bg-yellow-500/10">
                          <p class="text-2xl font-bold text-yellow-600">0</p>
                          <p class="text-xs text-muted-foreground">Pending</p>
                        </div>
                        <div class="text-center p-3 rounded-md bg-red-500/10">
                          <p class="text-2xl font-bold text-red-600">0</p>
                          <p class="text-xs text-muted-foreground">Errors</p>
                        </div>
                        <div class="text-center p-3 rounded-md bg-muted">
                          <p class="text-2xl font-bold">
                            {chans().filter((c) => !c.isMonitored).length}
                          </p>
                          <p class="text-xs text-muted-foreground">Paused</p>
                        </div>
                      </div>

                      <div class="rounded-lg border overflow-hidden">
                        <table class="w-full text-sm">
                          <thead class="bg-muted/50">
                            <tr>
                              <th class="text-left p-3 font-medium">Status</th>
                              <th class="text-left p-3 font-medium">Channel</th>
                              <th class="text-left p-3 font-medium">Videos</th>
                              <th class="text-left p-3 font-medium">Last Check</th>
                              <th class="text-left p-3 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={chans()}>
                              {(channel) => (
                                <tr class="border-t hover:bg-muted/30">
                                  <td class="p-3">
                                    {getStatusIcon(channel.isMonitored ? "healthy" : "paused")}
                                  </td>
                                  <td class="p-3">
                                    <A
                                      href={`/explore/sources/${channel.id}`}
                                      class="font-medium hover:text-primary"
                                    >
                                      {channel.name}
                                    </A>
                                    <p class="text-xs text-muted-foreground">
                                      {channel.handle}
                                    </p>
                                  </td>
                                  <td class="p-3">{channel.videoCount}</td>
                                  <td class="p-3 text-muted-foreground">
                                    {channel.lastVideoDate
                                      ? formatRelativeTime(channel.lastVideoDate)
                                      : "Never"}
                                  </td>
                                  <td class="p-3">
                                    <div class="flex gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="icon-sm" 
                                        title="Retry ingestion"
                                        onClick={handleRefreshStatus}
                                      >
                                        <RefreshCw class="h-3 w-3" />
                                      </Button>
                                      <Show
                                        when={channel.isMonitored}
                                        fallback={
                                          <Button 
                                            variant="ghost" 
                                            size="icon-sm" 
                                            title="Resume monitoring"
                                            onClick={() => handleToggleMonitoring(channel.id, false)}
                                            disabled={togglingChannelId() === channel.id}
                                          >
                                            <Show 
                                              when={togglingChannelId() === channel.id} 
                                              fallback={<Play class="h-3 w-3" />}
                                            >
                                              <Loader2 class="h-3 w-3 animate-spin" />
                                            </Show>
                                          </Button>
                                        }
                                      >
                                        <Button 
                                          variant="ghost" 
                                          size="icon-sm" 
                                          title="Pause monitoring"
                                          onClick={() => handleToggleMonitoring(channel.id, true)}
                                          disabled={togglingChannelId() === channel.id}
                                        >
                                          <Show 
                                            when={togglingChannelId() === channel.id} 
                                            fallback={<Pause class="h-3 w-3" />}
                                          >
                                            <Loader2 class="h-3 w-3 animate-spin" />
                                          </Show>
                                        </Button>
                                      </Show>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </For>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </Show>
              </Suspense>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle class="text-lg">Add New Channel</CardTitle>
            </CardHeader>
            <CardContent>
              <div class="flex gap-2">
                <Input 
                  placeholder="YouTube channel URL or @handle" 
                  class="flex-1"
                  value={channelUrl()}
                  onInput={(e) => setChannelUrl(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddChannel()}
                />
                <Button onClick={handleAddChannel} disabled={isAdding() || !channelUrl().trim()}>
                  <Show when={isAdding()} fallback={<Plus class="h-4 w-4 mr-1" />}>
                    <Loader2 class="h-4 w-4 mr-1 animate-spin" />
                  </Show>
                  Add Channel
                </Button>
              </div>
              <p class="text-xs text-muted-foreground mt-2">
                Enter a YouTube channel URL (e.g., youtube.com/@channelname) or handle
              </p>
            </CardContent>
          </Card>

          <div class="flex justify-end">
            <Button onClick={handleSaveSettings}>
              <Save class="h-4 w-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function HealthSkeleton() {
  return (
    <div class="space-y-4">
      <div class="grid grid-cols-4 gap-4">
        <Skeleton class="h-16" />
        <Skeleton class="h-16" />
        <Skeleton class="h-16" />
        <Skeleton class="h-16" />
      </div>
      <Skeleton class="h-48" />
    </div>
  );
}
